
import packageJson from '@/../package.json';
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";

import { IonMenu, IonHeader, IonContent, IonToolbar, IonTitle, IonItem, IonList, IonLabel, IonIcon, menuController } from "@ionic/vue";

import {
    documents,
    time,
    person,
    clipboard,
    car,
    copy,
    ticket,
    wallet,
    card,
    people,
    book,
    informationCircle,
    documentText,
    listCircle,
    hourglass,
    newspaper,
    calendar,
    cube,
    home,
    list,
    barcode,
    shareSocial,
    notificationsOutline,
} from "ionicons/icons";

import { config_menu } from "@/custom_config.js";
import { getAssetUrl } from "@/utils.js";

import { getUser } from "@/services/userStore";
import { openToast } from "@/services/toast";
import apiPush from "@/services/push";
import apiMobileSettings from "@/services/app_mobile_settings";

export default {
    components: {
        IonMenu,
        IonHeader,
        IonContent,
        IonToolbar,
        IonTitle,
        IonItem,
        IonList,
        IonLabel,
        IonIcon,
    },
    setup() {
        const appVersion = packageJson.version ?  packageJson.version : '1.0.0';

        const router = useRouter();
        const menu = ref(null);
        const notificationsEnabled = ref(false);
        const showNotificationButton = ref(false);
        const isEnablingNotifications = ref(false);

        const filteredMenuItems = computed(() => {
            const user = getUser();

            if (!user) return [];

            // Se il ruolo dell'utente non è incluso in allowedRoles, usa 'Employee' come fallback
            const userRole = config_menu.some((item) => item.allowedRoles.includes(user.users_type_value)) ? user.users_type_value : "Employee";

            return config_menu.filter((item) => item.show && item.allowedRoles.includes(userRole));
        });

        const changePage = (pageName) => {
            menuController.close("app-menu");
            router.push(`${pageName}`);
        };

        //Al decimo click mando al simulatore
        const counter = ref(0);

        const openSimulator = () => {
            counter.value++;

            if (counter.value === 10) {
                counter.value = 0; // reset counter
                menuController.close("app-menu");
                router.push("/simulatore");
            }
        };

        // Converte la VAPID public key in Uint8Array
        const urlBase64ToUint8Array = (base64String) => {
            const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
            const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

            const rawData = window.atob(base64);
            const outputArray = new Uint8Array(rawData.length);

            for (let i = 0; i < rawData.length; ++i) {
                outputArray[i] = rawData.charCodeAt(i);
            }
            return outputArray;
        };

        // Controlla se le notifiche sono già abilitate
        const checkIfNotificationsEnabled = async () => {
            if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
                return false;
            }

            try {
                const registration = await navigator.serviceWorker.ready;
                const subscription = await registration.pushManager.getSubscription();
                return subscription !== null;
            } catch (error) {
                console.error('Error checking notification subscription:', error);
                return false;
            }
        };

        // Abilita le notifiche push
        const enableNotifications = async () => {
            // Verifica supporto browser
            if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
                openToast("Le notifiche push non sono supportate su questo dispositivo", "toast_danger");
                return;
            }

            // Verifica utente loggato
            const user = getUser();
            if (!user || !user.dipendenti_user_id) {
                openToast("Devi essere loggato per abilitare le notifiche", "toast_danger");
                return;
            }

            isEnablingNotifications.value = true;

            try {
                // Richiedi permesso notifiche
                const permission = await Notification.requestPermission();

                if (permission !== "granted") {
                    openToast("Permesso notifiche negato", "toast_danger");
                    return;
                }

                // Attendi che il service worker sia pronto
                const registration = await navigator.serviceWorker.ready;

                // Recupera la VAPID public key dal backend
                const vapidResponse = await apiMobileSettings.getVapidPublicKey();

                if (vapidResponse.data.status !== 0 || !vapidResponse.data.data || !vapidResponse.data.data.length) {
                    openToast("Chiavi VAPID non configurate nel CRM", "toast_danger");
                    return;
                }

                const vapidPublicKey = vapidResponse.data.data[0].app_mobile_settings_vapid_public_key;

                if (!vapidPublicKey) {
                    openToast("Chiave VAPID mancante nelle impostazioni app mobile", "toast_danger");
                    return;
                }

                // Crea la subscription push
                const subscription = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
                });

                const subJson = subscription.toJSON();

                // Salva la subscription nel CRM
                try {
                    await apiPush.saveSubscription(
                        user.dipendenti_user_id,
                        subJson.endpoint,
                        subJson.keys?.p256dh,
                        subJson.keys?.auth
                    );
                    
                    openToast("Notifiche push abilitate correttamente", "toast_success");
                    notificationsEnabled.value = true;
                    showNotificationButton.value = false;
                    menuController.close("app-menu");
                } catch (e) {
                    console.error(e);
                    openToast("Errore nel salvataggio della sottoscrizione push", "toast_danger");
                }
            } catch (error) {
                console.error(error);
                openToast("Errore durante l'attivazione delle notifiche push", "toast_danger");
            } finally {
                isEnablingNotifications.value = false;
            }
        };

        // Verifica se l'app è già installata (standalone mode)
        const isStandalone = () => {
            return (
                window.matchMedia('(display-mode: standalone)').matches || // Android Chrome
                (window.navigator as any).standalone === true // iOS Safari
            );
        };

        // Controlla lo stato delle notifiche al mount
        onMounted(async () => {
            // Il pulsante viene mostrato solo se:
            // 1. Il browser supporta le notifiche push
            // 2. Le notifiche non sono abilitate
            // 
            // NOTA: Non mostriamo il pulsante agli utenti esistenti che hanno installato l'app PRIMA di questa integrazione
            // Per distinguerli, controlliamo se il flag first_standalone_open esiste
            // Se NON esiste E l'app è standalone, significa che hanno installato PRIMA di questa integrazione
            
            if ("serviceWorker" in navigator && "PushManager" in window) {
                const firstStandaloneOpen = localStorage.getItem('first_standalone_open') === 'true';
                const appIsStandalone = isStandalone();
                
                // Se l'app è standalone E first_standalone_open non esiste, significa che hanno installato PRIMA di questa integrazione
                // In questo caso NON mostriamo il pulsante per non disturbare utenti esistenti
                if (appIsStandalone && !firstStandaloneOpen) {
                    showNotificationButton.value = false;
                    return;
                }

                try {
                    const enabled = await checkIfNotificationsEnabled();
                    notificationsEnabled.value = enabled;
                    // Mostra il pulsante se le notifiche non sono abilitate
                    showNotificationButton.value = !enabled;
                } catch (error) {
                    console.error('Errore controllo notifiche:', error);
                    // In caso di errore, mostra il pulsante solo se non è un utente esistente
                    showNotificationButton.value = !(appIsStandalone && !firstStandaloneOpen);
                }
            } else {
                // Browser non supporta notifiche push
                showNotificationButton.value = false;
            }
        });

        return {
            documents,
            time,
            person,
            clipboard,
            hourglass,
            car,
            router,
            copy,
            documentText,
            ticket,
            wallet,
            people,
            listCircle,
            card,
            book,
            newspaper,
            calendar,
            cube,
            informationCircle,
            home,
            list,
            barcode,
            shareSocial,
            notificationsOutline,
            changePage,
            openSimulator,
            //configMenu
            config_menu,
            filteredMenuItems,
            menu,
            getAssetUrl,
            appVersion,
            notificationsEnabled,
            showNotificationButton,
            enableNotifications,
            isEnablingNotifications,
        };
    },
};
